import { X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { NotificationTypeIcon } from "@/components/ui/notification-type-icon";
import type { INotification } from "@/interfaces/notification.interface";

export interface NotificationQuickViewModalProps {
  open: boolean;
  onClose: () => void;
  notifications: INotification[];
  onClearAll?: () => void;
  onViewAll?: () => void;
}

export function NotificationQuickViewModal({
  open,
  onClose,
  notifications,
  onClearAll,
  onViewAll,
}: NotificationQuickViewModalProps) {
  // Show only the 3 most recent notifications
  const recentNotifications = notifications.slice(0, 3);

  const getTimeAgo = (date: string) => {
    // Simple time ago calculation (can be enhanced with a library)
    const now = new Date();
    const notificationDate = new Date(date);
    const diffInHours = Math.floor((now.getTime() - notificationDate.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) return "Agora";
    if (diffInHours === 1) return "1 hora atrás";
    if (diffInHours < 24) return `${diffInHours} horas atrás`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return "1 dia atrás";
    return `${diffInDays} dias atrás`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#15203C] border-[#3D3D3D] rounded-[20px] p-[30px] max-w-md">
        <DialogHeader className="relative">
          <div className="flex items-center justify-between">
            <DialogTitle className="font-['Plus_Jakarta_Sans'] font-bold text-white text-[18px]">
              Notificações ({recentNotifications.length.toString().padStart(2, '0')})
            </DialogTitle>
            {onClearAll && (
              <button
                onClick={onClearAll}
                className="font-['Plus_Jakarta_Sans'] font-normal text-[#A085FF] text-[14px] underline hover:text-[#B096FF] transition-colors"
              >
                Limpar todos
              </button>
            )}
          </div>
          <button
            onClick={onClose}
            className="absolute right-0 top-0 text-white hover:text-gray-300 transition-colors"
            aria-label="Fechar"
          >
            <X className="size-[24px]" />
          </button>
        </DialogHeader>

        <div className="mt-[20px] space-y-0">
          {recentNotifications.map((notification, index) => (
            <div key={notification.id}>
              <div className="flex gap-[15px] items-start py-[15px]">
                {/* Notification Type Icon */}
                <div className="shrink-0 mt-[2px]">
                  <NotificationTypeIcon type={notification.type} size="sm" />
                </div>

                {/* Notification Content */}
                <div className="flex-1 min-w-0">
                  <p className="font-['Plus_Jakarta_Sans'] font-bold text-white text-[16px] leading-normal">
                    {notification.message}
                  </p>
                  <p className="font-['Plus_Jakarta_Sans'] font-normal text-[#888888] text-[14px] leading-normal mt-[4px]">
                    {getTimeAgo(notification.date)}
                  </p>
                </div>

                {/* Unread Indicator */}
                {!notification.read && (
                  <div className="shrink-0">
                    <div className="size-[13px] rounded-full bg-[#7846FF]" />
                  </div>
                )}
              </div>

              {/* Divider (except for last item) */}
              {index < recentNotifications.length - 1 && (
                <div className="h-0 w-full relative">
                  <div className="absolute bottom-0 left-0 right-0 top-[-1px] border-b border-[#2a2f4a]" />
                </div>
              )}
            </div>
          ))}

          {/* Empty State */}
          {recentNotifications.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-400 text-sm">Nenhuma notificação recente</p>
            </div>
          )}
        </div>

        {/* Footer Link */}
        {onViewAll && recentNotifications.length > 0 && (
          <div className="mt-[20px] pt-[20px] border-t border-[#2a2f4a]">
            <button
              onClick={onViewAll}
              className="font-['Plus_Jakarta_Sans'] font-normal text-[#A085FF] text-[16px] underline hover:text-[#B096FF] transition-colors w-full text-center"
            >
              Ver todas notificações
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
